import { Component } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import CircularProgress from "@mui/material/CircularProgress";
import Icon from "@mui/material/Icon";
import InputAdornment from "@mui/material/InputAdornment";
import Pagination from "@mui/material/Pagination";
import Autocomplete from "@mui/material/Autocomplete";
import { Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions } from "@mui/material";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";

import DashboardLayout from "ScreenLayouts/ContainerLayout/DashboardLayout";
import DashboardNavbar from "ScreenLayouts/NavbarLayout/DashboardNavbar";
import Footer from "ScreenFooters/DefaultFooter";

import { request } from "app/network";
import ExerciseVideoCard from "./components/ExerciseVideoCard/ExerciseVideoCard";
import AddExerciseVideoModal from "./components/AddExerciseVideoModal/AddExerciseVideoModal";
import ReorderVideoModal from "./components/ReorderVideoModal/ReorderVideoModal";
import { useMaterialUIController } from "context";

interface ExerciseVideoListState {
    videos: any[];
    loading: boolean;
    searchTerm: string;
    pageSize: number;
    pageNumber: number;
    totalCount: number;
    isAddModalOpen: boolean;
    reorderModalOpen: boolean;
    editingVideo: any | null;
    isDeleteDialogOpen: boolean;
    deletingVideo: any | null;
    deleteError: string | null;
}

const PAGE_SIZE_STORAGE_KEY = "mediinstep_exercise_video_list_page_size";

class ExerciseVideoListScreenClass extends Component<any, ExerciseVideoListState> {
    constructor(props: any) {
        super(props);
        const savedPageSize = localStorage.getItem(PAGE_SIZE_STORAGE_KEY);
        const initialPageSize = savedPageSize ? parseInt(savedPageSize, 10) : 20;

        this.state = {
            videos: [],
            loading: false,
            searchTerm: "",
            pageSize: initialPageSize,
            pageNumber: 1,
            totalCount: 0,
            isAddModalOpen: false,
            reorderModalOpen: false,
            editingVideo: null,
            isDeleteDialogOpen: false,
            deletingVideo: null,
            deleteError: null,
        };
    }

    componentDidMount() {
        this.loadVideos();
    }

    loadVideos = async () => {
        try {
            this.setState({ loading: true });

            // 로그인 시 선택한 테넌트 가져오기
            const SELECTED_TENANT_KEY = "mediinstep_selected_tenant";
            const savedTenantId = localStorage.getItem(SELECTED_TENANT_KEY);

            const param = {
                PageNumber: this.state.pageNumber,
                PageSize: this.state.pageSize,
                SearchKeyword: this.state.searchTerm || undefined,
                TenantSeqId: savedTenantId ? parseInt(savedTenantId, 10) : undefined,
            };

            const [videoRes, bodyPartRes] = await Promise.all([
                request("ExerciseVideo.pagedList", { paramz: param }),
                request("BodyPart.pagedList", { paramz: { PageNumber: 1, PageSize: 1000 } })
            ]);

            const bodyPartMap: Record<number, string> = {};
            if (bodyPartRes && bodyPartRes.data && bodyPartRes.data.items) {
                bodyPartRes.data.items.forEach((bp: any) => {
                    bodyPartMap[bp.seqId] = bp.name;
                });
            }

            if (videoRes && videoRes.data) {
                const pagedList = videoRes.data;
                const videos = (pagedList.items || []).map((video: any) => ({
                    seqId: video.seqId,
                    title: video.title || "-",
                    description: video.description || "",
                    youtubeUrl: video.youtubeUrl || "",
                    youtubeVideoId: video.youtubeVideoId || "",
                    thumbnailUrl: video.thumbnailUrl || `https://img.youtube.com/vi/${video.youtubeVideoId}/mqdefault.jpg`,
                    durationSec: video.durationSec,
                    category: video.category,
                    difficultyLevel: video.difficultyLevel,
                    isActive: video.isActive,
                    isFeatured: video.isFeatured,
                    viewCount: video.viewCount || 0,
                    likeCount: video.likeCount || 0,
                    tenantName: video.tenantName,
                    crtDt: video.crtDt ? (video.crtDt.endsWith("Z") ? video.crtDt : `${video.crtDt}Z`) : video.crtDt,
                    thumbnailUrl43: video.thumbnailUrl43,
                    thumbnailUrl169: video.thumbnailUrl169,
                    bodyPartIds: video.bodyPartIds || [],
                    bodyPartNames: (video.bodyPartIds || []).map((id: number) => bodyPartMap[id] || "").filter((name: string) => name),
                }));

                this.setState({
                    videos,
                    totalCount: pagedList.totalCount || 0,
                    loading: false,
                });
            } else {
                this.setState({
                    videos: [],
                    totalCount: 0,
                    loading: false
                });
            }
        } catch (error: any) {
            console.error("운동 영상 목록 조회 실패:", error);
            this.setState({ loading: false });
        }
    };

    handleVideoClick = (video: any) => {
        console.log("영상 클릭:", video);
        // TODO: 영상 상세 페이지로 이동 또는 다이얼로그 열기
        if (video.youtubeUrl) {
            window.open(video.youtubeUrl, '_blank');
        }
    };

    handleOpenDeleteDialog = (video: any) => {
        this.setState({ deletingVideo: video, isDeleteDialogOpen: true });
    };

    handleCloseDeleteDialog = () => {
        this.setState({ isDeleteDialogOpen: false, deletingVideo: null, deleteError: null });
    };

    handleConfirmDelete = async () => {
        if (!this.state.deletingVideo) return;

        try {
            this.setState({ loading: true, deleteError: null });
            await request('ExerciseVideo.delete', {
                paramz: {},
                pathParams: { id: this.state.deletingVideo.seqId.toString() }
            });
            this.setState({ loading: false, isDeleteDialogOpen: false, deletingVideo: null });
            this.loadVideos();
        } catch (error: any) {
            console.error('영상 삭제 실패:', error);
            this.setState({ loading: false, deleteError: error?.response?.data?.message || '영상 삭제 중 오류가 발생했습니다.' });
        }
    };

    handleEdit = (video: any) => {
        this.setState({
            editingVideo: video,
            isAddModalOpen: true,
        });
    };

    handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
        const searchTerm = event.target.value;
        this.setState({ searchTerm, pageNumber: 1 }, () => {
            if (this.timeoutId) clearTimeout(this.timeoutId);
            this.timeoutId = setTimeout(this.loadVideos, 500);
        });
    };

    handlePageChange = (pageIndex: number) => {
        this.setState({ pageNumber: pageIndex + 1 }, () => {
            this.loadVideos();
        });
    };

    handlePageSizeChange = (pageSize: number) => {
        localStorage.setItem(PAGE_SIZE_STORAGE_KEY, pageSize.toString());
        this.setState({ pageSize, pageNumber: 1 }, () => {
            this.loadVideos();
        });
    };

    private timeoutId: any = null;

    render() {
        const { loading, searchTerm, totalCount, pageSize, pageNumber } = this.state;

        return (
            <DashboardLayout>
                <DashboardNavbar />
                <MDBox pt={6} pb={3}>
                    {/* 검색 영역 - Card 배경 유지 */}
                    <Card>
                        <MDBox p={3}>
                            <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                                <MDBox display="flex" alignItems="center">
                                    <Icon fontSize="medium">video_library</Icon>
                                    <MDTypography ml={1} variant="h5" fontWeight="medium">
                                        운동 영상 목록 ( EXERCISE VIDEO LIST )
                                    </MDTypography>
                                </MDBox>
                                <MDBox display="flex" gap={1}>
                                    {/* 영상이 2개 이상일 때만 순서 변경 버튼 표시 */}
                                    {this.state.totalCount >= 2 && (
                                        <MDButton
                                            variant="outlined"
                                            color="info"
                                            onClick={() => this.setState({ reorderModalOpen: true })}
                                        >
                                            <Icon fontSize="small" sx={{ mr: 0.5 }}>
                                                swap_vert
                                            </Icon>
                                            영상 순서 변경
                                        </MDButton>
                                    )}
                                    <MDButton variant="gradient" color="info" onClick={() => this.setState({ isAddModalOpen: true })}>
                                        <Icon fontSize="small" sx={{ mr: 0.5 }}>
                                            add
                                        </Icon>
                                        영상 추가
                                    </MDButton>
                                </MDBox>
                            </MDBox>

                            <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                                <MDBox display="flex" alignItems="center">
                                    <Autocomplete
                                        disableClearable
                                        value={this.state.pageSize.toString()}
                                        options={["5", "10", "15", "20", "25", "30", "40", "50"]}
                                        renderInput={(params) => (
                                            <MDInput {...params} />
                                        )}
                                        size="small"
                                        sx={{ width: "5rem" }}
                                        onChange={(_, newValue) => {
                                            if (newValue) {
                                                this.handlePageSizeChange(Number(newValue));
                                            }
                                        }}
                                    />
                                    <MDTypography variant="caption" color="secondary">
                                        &nbsp;&nbsp;entries per page
                                    </MDTypography>
                                </MDBox>

                                <MDBox width="12rem" ml="auto">
                                    <MDInput
                                        placeholder="Video Search..."
                                        value={searchTerm}
                                        onChange={this.handleSearch}
                                        size="small"
                                        fullWidth
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <Icon fontSize="small" sx={{ color: '#9e9e9e' }}>
                                                        search
                                                    </Icon>
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                </MDBox>
                            </MDBox>
                        </MDBox>
                    </Card>

                    {/* 리스트 영역 - 배경 없음 */}
                    <MDBox mt={3}>
                        {loading ? (
                            <MDBox
                                display="flex"
                                justifyContent="center"
                                alignItems="center"
                                minHeight="400px"
                            >
                                <MDBox display="flex" gap={1.5}>
                                    <CircularProgress size={40} sx={{ color: "#49a3f1" }} />
                                </MDBox>
                            </MDBox>
                        ) : this.state.videos.length === 0 ? (
                            <MDBox
                                display="flex"
                                justifyContent="center"
                                alignItems="center"
                                minHeight="400px"
                            >
                                <MDTypography variant="h6" color="text">
                                    {searchTerm ? "검색 결과가 없습니다." : "등록된 운동 영상이 없습니다."}
                                </MDTypography>
                            </MDBox>
                        ) : (
                            <>
                                <Grid container spacing={3}>
                                    {this.state.videos.map((video) => {
                                        // 사이드바 상태에 따라 다른 Grid 설정
                                        const gridProps = this.props.miniSidenav
                                            ? { xs: 12, sm: 6, md: 4, lg: 4, xl: 2.4 } // 사이드바 닫힘: ~1200px: 3개, 1536px~: 5개
                                            : { xs: 12, sm: 6, md: 4, lg: 4, xl: 4 }; // 사이드바 열림: 모든 화면에서 3개 유지

                                        // 1920px 이상에서 추가 설정
                                        const sxProps = this.props.miniSidenav
                                            ? {
                                                '@media (min-width: 1920px)': {
                                                    flexBasis: '16.666667%', // 6개 (12/6 = 2)
                                                    maxWidth: '16.666667%'
                                                }
                                            }
                                            : {
                                                '@media (min-width: 1920px)': {
                                                    flexBasis: '20%', // 5개 (12/5 = 2.4)
                                                    maxWidth: '20%'
                                                }
                                            };

                                        return (
                                            <Grid item {...gridProps} sx={sxProps} key={video.seqId}>
                                                <ExerciseVideoCard
                                                    video={video}
                                                    onClick={this.handleVideoClick}
                                                    onEdit={this.handleEdit}
                                                    onDelete={() => this.handleOpenDeleteDialog(video)}
                                                    darkMode={this.props.darkMode}
                                                />
                                            </Grid>
                                        );
                                    })}
                                </Grid>

                                {totalCount > 0 && (
                                    <MDBox mt={4} display="flex" justifyContent="center" alignItems="center" gap={2}>
                                        <Pagination
                                            count={Math.ceil(totalCount / pageSize)}
                                            page={pageNumber}
                                            onChange={(_, page) => this.handlePageChange(page - 1)}
                                            color="primary"
                                            size="large"
                                            showFirstButton
                                            showLastButton
                                            sx={{
                                                '& .MuiPaginationItem-root': {
                                                    color: this.props.darkMode ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.7)',
                                                },
                                                '& .Mui-selected': {
                                                    backgroundColor: '#49a3f1 !important',
                                                    color: '#ffffff !important',
                                                },
                                            }}
                                        />
                                    </MDBox>
                                )}
                            </>
                        )}
                    </MDBox>
                </MDBox>
                <Footer />
                <AddExerciseVideoModal
                    open={this.state.isAddModalOpen}
                    editingVideo={this.state.editingVideo}
                    onClose={() => this.setState({ isAddModalOpen: false, editingVideo: null })}
                    onSuccess={() => {
                        this.setState({ isAddModalOpen: false, editingVideo: null });
                        this.loadVideos();
                    }}
                    darkMode={this.props.darkMode}
                />

                {/* 영상 순서 변경 모달 */}
                <ReorderVideoModal
                    open={this.state.reorderModalOpen}
                    onClose={() => this.setState({ reorderModalOpen: false })}
                    onSuccess={this.loadVideos}
                    videos={this.state.videos.map(video => ({
                        seqId: video.seqId,
                        title: video.title,
                        thumbnailUrl: video.thumbnailUrl,
                        displayOrder: video.displayOrder || this.state.videos.findIndex(v => v.seqId === video.seqId) + 1,
                    }))}
                    darkMode={this.props.darkMode}
                />

                {/* 삭제 확인 다이얼로그 */}
                <Dialog
                    open={this.state.isDeleteDialogOpen}
                    onClose={this.handleCloseDeleteDialog}
                    PaperProps={{
                        sx: {
                            backgroundColor: this.props.darkMode ? '#1a2035' : '#fff',
                            backgroundImage: 'none',
                        }
                    }}
                >
                    <DialogTitle sx={{ color: this.props.darkMode ? '#fff' : '#000' }}>
                        영상 삭제 확인
                    </DialogTitle>
                    <DialogContent>
                        <DialogContentText sx={{ color: this.props.darkMode ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)' }}>
                            "{this.state.deletingVideo?.title}" 영상을 삭제하시겠습니까?<br />
                            이 작업은 취소할 수 없습니다.
                        </DialogContentText>
                        {this.state.deleteError && (
                            <MDBox mt={2}>
                                <p style={{ color: '#f44336', fontSize: '0.875rem', margin: 0 }}>
                                    {this.state.deleteError}
                                </p>
                            </MDBox>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <MDButton onClick={this.handleCloseDeleteDialog} color="secondary">
                            취소
                        </MDButton>
                        <MDButton
                            onClick={this.handleConfirmDelete}
                            variant="gradient"
                            color="error"
                            disabled={loading}
                        >
                            {loading ? '삭제 중...' : '삭제'}
                        </MDButton>
                    </DialogActions>
                </Dialog>
            </DashboardLayout>
        );
    }
}

function ExerciseVideoListScreen(props: any) {
    const [controller] = useMaterialUIController();
    const { darkMode, miniSidenav } = controller;

    return <ExerciseVideoListScreenClass {...props} darkMode={darkMode} miniSidenav={miniSidenav} />;
}

export default ExerciseVideoListScreen;
